import React, { useState } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { CreditCard, SealPercent, Truck } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { classNames } from "lib/utils/helpers";
import { CouponPageConfigType } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "@shopflo/ui/utils";

interface CalloutCardProps {
  isApplicable: boolean;
  couponTitle: string;
  couponHeader: string;
  couponDescription: string;
  couponLongDescription: string[];
  unApplicabilityReason: string;
  couponType?: string;
  customisations?: CouponPageConfigType;
}

const CalloutCard: React.FC<CalloutCardProps> = React.memo(
  ({
    isApplicable,
    couponTitle,
    couponHeader,
    couponDescription,
    couponLongDescription,
    unApplicabilityReason,
    couponType,
    customisations,
  }) => {
    const { t } = useLocale();
    const [showMore, setShowMore] = useState<boolean>(false);

    const greyedOut = !isApplicable;

    function getCouponIcon(couponType: string, greyedOut: boolean, primaryColor?: string) {
      let IconComponent: typeof Truck | typeof CreditCard | typeof SealPercent = SealPercent;
      switch (couponType) {
        case 'SHIPPING_OFFERS':
          IconComponent = Truck;
          break;
        case 'PAYMENT_OFFERS':
          IconComponent = CreditCard;
          break;
        default:
          IconComponent = SealPercent;
      }
      return (
        <Icon
          IconComponent={IconComponent}
          size="sm"
          style={{
            color: greyedOut ? 'var(--flo-gray-dark-color)' : primaryColor ?? undefined,
          }}
          iconProps={{
            weight:"fill"
          }}
        />
      );
    }

    return (
      <div className="group pb-3 relative box-border flex h-full w-full flex-col items-start overflow-hidden rounded-2xl border border-zinc-200 bg-white"
        style={{
          borderRadius: customisations?.cardRadius,
        }}
      >
        <div
          className={classNames(
            "flex w-full flex-row items-center justify-between py-2 pl-3 pr-2",
            greyedOut ? "" : "",
          )}
          style={{
            background: greyedOut
              ? undefined
              : `linear-gradient(to bottom, ${hexToRgba(customisations?.primaryColor ?? '#000000', 0.2)}, #FFF)`,
          }}>
          <div className="flex flex-row items-center gap-1">
            <div className="flex items-center justify-start">
              {getCouponIcon(couponType ?? 'DEFAULT', greyedOut, customisations?.primaryColor ?? '#000000')}
            </div>

            <h3 className="truncate pr-2 text-text-sm-semibold uppercase">
              {couponTitle}
            </h3>
          </div>
        </div>

        <div className="group relative flex w-full flex-col items-start gap-2 overflow-x-hidden px-3">
          <div className="flex flex-col">
            <div className="flex flex-col">
              <h3 className="text-text-xs-semibold" style={{ color: 'var(--flo-coal-dark-color)' }}>{couponHeader}</h3>
              {couponDescription && Boolean(couponDescription.length) && (
                <p className="text-text-xs-medium" style={{ color: 'var(--flo-coal-light-color)' }}>{couponDescription}</p>
              )}
            </div>
          </div>

          {greyedOut && Boolean(unApplicabilityReason) && (
            <p className="text-xs font-medium text-ouch">{unApplicabilityReason}</p>
          )}

          {couponLongDescription && couponLongDescription.length > 0 && (
            showMore ? (
              <>
                <ul className="flex w-full flex-col space-y-1 px-2 py-3">
                  {couponLongDescription?.map((point: string, index: number) => (
                    <li
                      className="ml-4 list-disc text-xs font-normal text-coal-light"
                      key={`Callout-Card-${index}`}>
                      <p>{point}</p>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setShowMore(false)}
                  className="w-fit text-text-xxs-medium outline-none"
                  style={{ color: 'var(--flo-coal-dark-color)' }}>
                  Hide details
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowMore(true)}
                className="w-fit text-text-xxs-medium outline-none text-zinc-500">
                {t("more")}
              </button>
            )
          )}
        </div>
      </div>
    );
  },
);

export default CalloutCard;
